import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { showModal } from '../../../../redux/system/actions';
import ConfirmModal from '../../../common/modals/ConfirmModal';
import { getAllSkillSets } from '../../../../api/curriculumVitaeApi';
import { toast } from 'react-toastify';
import { COMPLETE_CURRICULUM_VITAE_SKILL_TOAST_MESSAGES, getErrorUpdateToastPropsBy } from '../../../../service/toastService';

const CANCEL_MODAL_ID = 'cancelModal';

const validationSchema = Yup.object().shape({
  skillName: Yup.array().min(1, 'Skill name is required'),
  skillType: Yup.string().required('Skill type is required'),
  skillRating: Yup.string().when('skillType', {
    is: (skillType) => skillType !== '2' && skillType !== '',
    then: (schema) => schema.required('Skill rating is required'),
    otherwise: (schema) => schema,
  }),
});

function SkillsForm({ curriculumVitaeId, initialSkill, setEditingSkill, setAddingSkill, onSave, status }) {
  const dispatch = useDispatch();
  const handleShowModal = useCallback((modalId) => dispatch(showModal(modalId)), [dispatch]);

  const [skill, setSkill] = useState(
    initialSkill || {
      skillName: [],
      skillType: '',
      workField: '',
      skillRating: '',
      curriculumVitaeId,
    }
  );

  const [selectedWorkField, setSelectedWorkField] = useState("");
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [isCustomSkill, setIsCustomSkill] = useState(false);
  const [customSkillName, setCustomSkillName] = useState("");
  const [skillSets, setSkillSets] = useState([]);

  const getWorkFieldBySkill = (skillName) => {
    const skillsArray = Array.isArray(skillName) ? skillName : [skillName];
    for (const [workField, skills] of Object.entries(skillSets)) {
      if (skillsArray.some(skill => skills.includes(skill))) {
        return workField;
      }
    }
    return "";
  };

  const clearForm = () => {
    setEditingSkill(null);
    setSkill({
      skillName: [],
      skillType: '',
      workField: '',
      skillRating: '',
    });
    setIsCustomSkill(false);
    setCustomSkillName("");
  };

  const handleCancel = () => {
    handleShowModal(CANCEL_MODAL_ID);
  };

  const confirmCancel = () => {
    setAddingSkill(false);
    setEditingSkill(null);
  };

  useEffect(() => {
    getAllSkillSets()
      .then(response => {
        const setData = response.data;

        const mapped = {};
        setData.forEach(entry => {
          if (!mapped[entry.workField]) {
            mapped[entry.workField] = [];
          }
          mapped[entry.workField].push(...entry.skillNames);
        });

        setSkillSets(mapped);
      });
  }, []);

  useEffect(() => {
    if (initialSkill && Object.keys(skillSets).length > 0) {
      const skillNames = Array.isArray(initialSkill.skillName)
        ? initialSkill.skillName
        : [initialSkill.skillName];

      const detectedWorkField = getWorkFieldBySkill(skillNames);
      const fallbackWorkField = initialSkill.workField || "";
      const finalWorkField = detectedWorkField || fallbackWorkField;
      const isPredefined = detectedWorkField !== "";

      setSkill({
        ...initialSkill,
        skillName: skillNames,
        skillRating: initialSkill.skillRating != null ? initialSkill.skillRating.toString() : '',
      });

      setSelectedWorkField(finalWorkField);
      setSelectedSkills(skillNames);
      setIsCustomSkill(!isPredefined);
      setCustomSkillName(isPredefined ? "" : skillNames[0]);
    }
  }, [initialSkill, skillSets]);


  return (
    <><Formik
      enableReinitialize={true}
      initialValues={{
        ...skill,
        curriculumVitaeId: skill.curriculumVitaeId || curriculumVitaeId,
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        if (isCustomSkill && customSkillName.trim() === "") {
          toast.error('Custom skill name is required.', getErrorUpdateToastPropsBy(COMPLETE_CURRICULUM_VITAE_SKILL_TOAST_MESSAGES));
          return;
        }
        const finalSkillName = isCustomSkill
          ? [customSkillName]
          : selectedSkills;

        onSave({ ...values, skillName: finalSkillName });
        resetForm();
        setSelectedSkills([]);
        setCustomSkillName("");
        setIsCustomSkill(false);
        setSkill({
          skillName: [],
          skillType: '',
          workField: '',
          skillRating: '',
          curriculumVitaeId,
        });
      }}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <div className="row">
            <div className="col-md-6">
              <div className="form-group mt-3">
                <label className="small">SKILL SOURCE *</label>
                <div className="d-flex flex-column">
                  <label>
                    <input
                      type="radio"
                      checked={!isCustomSkill}
                      onChange={() => {
                        const detectedWorkField = getWorkFieldBySkill(selectedSkills);
                        setIsCustomSkill(false);
                        setSelectedWorkField(detectedWorkField || selectedWorkField);
                      }}
                      className="mr-2"
                    />
                    Select from List
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={isCustomSkill}
                      onChange={() => {
                        setIsCustomSkill(true);
                        setSelectedSkills([]);
                      }}
                      className="mr-2"
                    />
                    Custom Skill
                  </label>
                </div>
              </div>

              {isCustomSkill && (
                <div className="form-group">
                  <label htmlFor="customSkill" className="small">SKILL NAME *</label>
                  <input
                    type="text"
                    className="form-control w-100"
                    style={{ maxWidth: "400px" }}
                    id="customSkill"
                    value={customSkillName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomSkillName(value);
                      const newSkills = [value];
                      setSelectedSkills(newSkills);
                      setFieldValue("skillName", newSkills);
                    }}
                  />
                  <ErrorMessage name="skillName" component="div" className="text-danger small" />
                </div>
              )}

              {!isCustomSkill && (
                <div className="form-group">
                  <label htmlFor="workField" className="small">WORK FIELD *</label>
                  <Field
                    as="select"
                    className="form-control w-100"
                    style={{ maxWidth: "400px" }}
                    id="workField"
                    name="workField"
                    required
                    value={selectedWorkField}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedWorkField(value);
                      setSelectedSkills([]);
                      setFieldValue("workField", value);
                      setFieldValue("skillName", []);
                    }}
                  >
                    <option value="">Select work field</option>
                    {Object.keys(skillSets).map((field, idx) => (
                      <option key={idx} value={field}>
                        {field}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="workField" component="div" className="text-danger mt-1 small" />
                </div>
              )}

              <div className="form-group mt-3">
                <label htmlFor="skillType" className="small">SKILL TYPE *</label>
                <Field as="select" className="form-control w-100" id="skillType" name="skillType" style={{ maxWidth: "400px" }}>
                  <option disabled value="">Choose skill type</option>
                  <option value="0">Job related</option>
                  <option value="1">Hard skill</option>
                  <option value="2">Soft skill</option>
                </Field>
                <ErrorMessage name="skillType" component="div" className="text-danger mt-1 small" />
              </div>

              {values.skillType !== "2" && values.skillType !== "" && (
                <div className="form-group mt-3">
                  <label htmlFor="skillRating" className="small">SKILL RATING *</label>
                  <Field as="select" className="form-control w-100" id="skillRating" name="skillRating" style={{ maxWidth: "400px" }}>
                    <option disabled value="">Rate your skill</option>
                    <option value="0">Beginner</option>
                    <option value="1">Intermediate</option>
                    <option value="2">Advanced</option>
                    <option value="3">Expert</option>
                  </Field>
                  <ErrorMessage name="skillRating" component="div" className="text-danger mt-1 small" />
                </div>
              )}
            </div>

            <div className="col-md-6">
              {!isCustomSkill && selectedWorkField && skillSets[selectedWorkField] && (
                <>
                  <strong className="d-flex justify-content-center mb-3 h5">Select Skills:</strong>
                  <div
                    style={{
                      maxHeight: "330px",
                      overflowY: "auto",
                      paddingRight: "0.5rem",
                    }}
                  >
                    <div className="d-flex flex-wrap justify-content-center gap-2">
                      {skillSets[selectedWorkField].map((skill) => {
                        const isSelected = selectedSkills.includes(skill);
                        return (
                          <div
                            key={skill}
                            onClick={() => {
                              let updated = [];

                              if (initialSkill) {
                                updated = [skill];
                              } else {
                                updated = isSelected
                                  ? selectedSkills.filter((s) => s !== skill)
                                  : [...selectedSkills, skill];
                              }

                              setSelectedSkills(updated);
                              setFieldValue("skillName", updated);
                            }}

                            className={`list-group-item ${isSelected ? "active" : ""
                              }`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              padding: "1rem",
                              cursor: "pointer",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              flex: "1 1 calc(33% - 1rem)",
                              minWidth: "120px",
                              maxWidth: "180px",
                              height: "100px",
                            }}
                          >
                            {skill}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="d-flex flex-wrap mt-4 gap-3">
            {initialSkill ? (
              <div className="d-flex align-items-start">
                <button className="btn btn-primary d-flex align-items-center mt-2 mr-3" type="submit">
                  <i className="pe-7s-pen mr-2"></i>
                  <span>Update Skill</span>
                </button>

                {status === 1 ? (
                  <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                    <span>Cancel</span>
                  </button>
                ) : (
                  <button className="cancel-button d-flex align-items-center mt-2" type="button" onClick={clearForm}>
                    <span>Cancel</span>
                  </button>
                )}
              </div>
            ) : (
              <div className='d-flex'>
                <button className="btn btn-primary d-flex mt-2 mr-3" type="submit">
                  <i className="pe-7s-plus mr-2"></i>
                  <span>Add Skill</span>
                </button>
                {status === 1 && (
                  <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                    <span>Cancel</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </Form>

      )}
    </Formik>
      <ConfirmModal
        id={CANCEL_MODAL_ID}
        title="Are you sure you want to stop adding this skill?"
        message="This will cancel the form and return you to the overview. Do you wish to proceed?"
        onConfirm={confirmCancel} />
    </>
  );
}

export default SkillsForm;
